import { useMemo } from 'react';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { useBulkGroupListings } from 'src/components/Listings/InventoryActionDropdown/components/GroupListingsAction/useBulkGroupListings';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ChevronDownIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Listing } from 'src/WebApiController';

import { useInventoryEventBulkActionsState } from '../../InventoryEventBulkActions/useInventoryEventBulkActionsState';

export const GroupActions = () => {
  const { areBulkActionsEnabled } = useBulkActionsState();

  const { affectedVisibleListingIds, filterQueryWithEventIds } =
    useInventoryEventBulkActionsState();
  const { eventsTransformed } = useCatalogDataContext();

  const [canGroupListings, selectedEvents] = useMemo(() => {
    const { eventOrMappingIds } = filterQueryWithEventIds;
    const eventIdsFromSelectedListings = (eventsTransformed ?? [])
      .flatMap((ev) => ev.entities.listings)
      .flatMap(flattenListingGroup)
      .filter(
        ({ id, isLtGrp }) =>
          id > 0 && !isLtGrp && affectedVisibleListingIds.includes(id)
      )
      .map((l) => (l as Listing).viagVirtId);
    const selectedEvents = (eventsTransformed ?? [])
      .filter(
        (e) =>
          eventOrMappingIds?.includes(e.event.virtId) ||
          eventIdsFromSelectedListings.includes(e.event.virtId)
      )
      .map((e) => e.event);
    const uniqueEventVenueIds = [
      ...new Set(
        selectedEvents
          .map((e) => e.venCfgId)
          .filter((id): id is number => id != null)
      ),
    ];
    const sameEventConfig = uniqueEventVenueIds.length === 1;
    return [sameEventConfig, selectedEvents];
  }, [affectedVisibleListingIds, eventsTransformed, filterQueryWithEventIds]);

  const event = useMemo(() => {
    if (selectedEvents.length > 0) {
      return selectedEvents[0];
    }
    return undefined;
  }, [selectedEvents]);

  const bulkGroupListings = useBulkGroupListings(event, selectedEvents);

  return (
    <PosDropdown
      key="events-group-actions-dropdown"
      trigger={
        <Button variant="outline">
          <Content id={ContentId.Group} />
          <ChevronDownIcon
            size={vars.iconSize.xs}
            fill={IconsFill.currentColor}
          />
        </Button>
      }
      align="start"
      triggerProps={{
        disabled: !areBulkActionsEnabled || !canGroupListings,
      }}
    >
      {bulkGroupListings.dropdown}
    </PosDropdown>
  );
};
